import { Restaurant } from "api-types";

import React, { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { motionDurations, motionEasings } from "@dashboard/animations";
import BrandSvgRaw from "@dashboard/assets/images/icons/Brand.svg?raw";
import DishesSvgRaw from "@dashboard/assets/images/icons/Dishes.svg?raw";
import MenuSvgRaw from "@dashboard/assets/images/icons/Menu.svg?raw";
import SettingsSvgRaw from "@dashboard/assets/images/icons/Settings.svg?raw";
import InsightsSvgRaw from "@dashboard/assets/images/icons/insights.svg?raw";
import IntegrationsSvgRaw from "@dashboard/assets/images/icons/integration.svg?raw";
import ProductSvgRaw from "@dashboard/assets/images/icons/product.svg?raw";
import { INSIGHTS_NAV_LABEL } from "@dashboard/components/admin/allergen-insights/insightsCopy";
import { ICON_SIZE } from "@dashboard/components/common/StreamlineIcon";
import { BackIcon, QrCodeIcon } from "@dashboard/components/common/icons";
import { useNudgeDone } from "@dashboard/components/restaurant/guest-menu/guestMenuNudges";
import {
  useDashboardAppFeatureFlag,
  useFeatures,
} from "@dashboard/hooks/useFeature";
import { useGuestMenuVersion } from "@dashboard/hooks/useGuestMenuVersion";
import type { ReduxState } from "@dashboard/types";
import { DEFAULT_THEME_ID } from "@dashboard/types/theme";

type TabConfig = {
  label: React.ReactNode;
  ariaLabel: string;
  icon: React.ReactElement;
  path: string;
  dataDdActionName: string;
};

type SidebarIndicator = {
  top: number;
  height: number;
  visible: boolean;
};

let lastSidebarIndicator: SidebarIndicator | null = null;

// Every nav mark is drawn to a 24 grid at a 1.5 stroke, so they all render at
// one size. Scaling them individually is what pulled the strokes apart.
const NAV_ICON_SIZE = 24;

const InlineSvgIcon = ({ svg, size }: { svg: string; size: number }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: size,
      height: size,
      lineHeight: 0,
      color: "inherit",
      "& svg": {
        width: "100%",
        height: "100%",
        display: "block",
      },
    }}
    dangerouslySetInnerHTML={{ __html: svg }}
  />
);

const AdminTabLabel = ({ title }: { title: string }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    <Box component="span">{title}</Box>
    <Box
      component="span"
      className="igfd-admin-pill"
      sx={({ palette, spacing }) => ({
        borderRadius: 9999,
        fontSize: 10,
        lineHeight: "12px",
        padding: `${spacing(0.25)} ${spacing(1)}`,
        display: "inline-block",
        backgroundColor: palette.grey[200],
        color: palette.grey[700],
      })}
    >
      Admin
    </Box>
  </Box>
);

// Return the ordered tabs for the header sidebar
const getTabConfig = (
  hasProducts: boolean,
  canSeeIntegrations: boolean
): TabConfig[] => {
  let tabs: TabConfig[] = [
    {
      label: "Menus",
      ariaLabel: "Menus",
      icon: <InlineSvgIcon svg={MenuSvgRaw} size={NAV_ICON_SIZE} />,
      path: "/menus",
      dataDdActionName: "menu-page",
    },
  ];

  tabs.push({
    label: "Dishes",
    ariaLabel: "Dishes",
    icon: <InlineSvgIcon svg={DishesSvgRaw} size={NAV_ICON_SIZE} />,
    path: "/dishes",
    dataDdActionName: "dishes-page",
  });

  if (hasProducts) {
    tabs.push({
      label: "Products",
      ariaLabel: "Products",
      icon: <InlineSvgIcon svg={ProductSvgRaw} size={NAV_ICON_SIZE} />,
      path: "/products",
      dataDdActionName: "products-page",
    });
  }

  tabs.push({
    label: "Themes",
    ariaLabel: "Themes",
    icon: <InlineSvgIcon svg={BrandSvgRaw} size={NAV_ICON_SIZE} />,
    path: "/themes",
    dataDdActionName: "themes-page",
  });

  tabs.push({
    label: "Menu URLs",
    ariaLabel: "Menu URLs",
    icon: <QrCodeIcon size={NAV_ICON_SIZE} />,
    path: "/menu-urls",
    dataDdActionName: "menu-urls-page",
  });

  // Not gated: allergen and dietary suitability is for every restaurant, not
  // just Ingredifind staff.
  tabs.push({
    label: INSIGHTS_NAV_LABEL,
    ariaLabel: INSIGHTS_NAV_LABEL,
    icon: <InlineSvgIcon svg={InsightsSvgRaw} size={NAV_ICON_SIZE} />,
    path: "/insights",
    dataDdActionName: "insights-page",
  });

  if (canSeeIntegrations) {
    tabs.push({
      label: <AdminTabLabel title="Integrations" />,
      ariaLabel: "Integrations",
      icon: <InlineSvgIcon svg={IntegrationsSvgRaw} size={NAV_ICON_SIZE} />,
      path: "/integrations",
      dataDdActionName: "integrations-page",
    });
  }

  tabs.push({
    label: "Settings",
    ariaLabel: "Settings",
    icon: <InlineSvgIcon svg={SettingsSvgRaw} size={NAV_ICON_SIZE} />,
    path: "/settings",
    dataDdActionName: "settings-page",
  });

  return tabs;
};

const withDot = (icon: React.ReactElement, dot: boolean) =>
  dot ? (
    <Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
      {icon}
      <Box
        component="span"
        data-testid="settings-dot"
        sx={{
          position: "absolute",
          top: -2,
          right: -4,
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#3A09FF",
          boxShadow: "0 0 0 2px #FFFFFF",
        }}
      />
    </Box>
  ) : (
    icon
  );

export const HeaderSidebarComponent = ({
  isLargeScreen,
  isLeftDrawerOpen,
  toggleLeftDrawer,
  pathname,
  navigate,
  selectedRestaurant,
}: {
  isLargeScreen: boolean;
  isLeftDrawerOpen: boolean;
  toggleLeftDrawer: () => void;
  pathname: string;
  navigate: (to: string) => void;
  selectedRestaurant: Restaurant;
}) => {
  const { products } = useFeatures();
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );
  const showIntegrationsPage = useDashboardAppFeatureFlag(
    "showIntegrationsPage"
  );
  const newMenuOffered = useDashboardAppFeatureFlag("enableGuestMenuV2");
  const guestMenuVersion = useGuestMenuVersion();
  const [guestMenuOpened] = useNudgeDone("guestMenuOpened");
  // Until the manager has looked at the Guest menu row once.
  const settingsDot =
    newMenuOffered && guestMenuVersion === "classic" && !guestMenuOpened;
  const tabConfig = useMemo(
    () => getTabConfig(products, showIntegrationsPage && userIsAdmin),
    [products, showIntegrationsPage, userIsAdmin]
  );

  // Height-based responsive tiers for the vertical nav
  const isShortHeight = useMediaQuery("(max-height:900px)");
  const isVeryShortHeight = useMediaQuery("(max-height:780px)");
  const isUltraShortHeight = useMediaQuery("(max-height:680px)");

  const compact = isShortHeight;
  const hideLabels = isVeryShortHeight;
  const dense = isUltraShortHeight;
  const tabPaddingY = dense ? 0.5 : compact ? 1 : 2;
  const iconScale = dense ? 0.75 : 1;

  const selectedTabFromPath = useMemo(() => {
    const tabIdx = tabConfig.findIndex((tab) => pathname.startsWith(tab.path));
    return tabIdx === -1 ? false : tabIdx;
  }, [pathname, tabConfig]);
  const hasPreviousSidebarIndicator =
    selectedTabFromPath !== false && lastSidebarIndicator !== null;

  // Keep settings tab fixed at the bottom by applying marginTop: 'auto' on that tab
  const [tab, setTab] = React.useState<false | number>(selectedTabFromPath);
  const currentTabRef = React.useRef<false | number>(tab);
  const tabsRef = React.useRef<HTMLDivElement | null>(null);
  const sidebarIndicatorRef = React.useRef<SidebarIndicator | null>(null);
  const sidebarIndicatorAnimationFramesRef = React.useRef<{
    first?: number;
    second?: number;
  }>({});
  const deferFirstIndicatorMeasurement = React.useRef(
    hasPreviousSidebarIndicator
  );
  const [sidebarIndicator, setSidebarIndicator] =
    React.useState<SidebarIndicator>(() =>
      hasPreviousSidebarIndicator && lastSidebarIndicator
        ? lastSidebarIndicator
        : {
            top: 0,
            height: 0,
            visible: false,
          }
    );
  currentTabRef.current = tab;
  sidebarIndicatorRef.current = sidebarIndicator;

  useEffect(() => {
    setTab(selectedTabFromPath);
  }, [selectedTabFromPath]);

  const updateSidebarIndicator = useCallback((nextTab: false | number) => {
    const tabsNode = tabsRef.current;

    if (!tabsNode || nextTab === false) {
      setSidebarIndicator((current) =>
        current.visible ? { ...current, visible: false } : current
      );
      return;
    }

    const scrollerNode =
      tabsNode.querySelector<HTMLElement>(".MuiTabs-scroller") ?? tabsNode;
    const tabElements =
      scrollerNode.querySelectorAll<HTMLElement>('[role="tab"]');
    const selectedTab = tabElements[nextTab];

    if (!selectedTab) {
      setSidebarIndicator((current) =>
        current.visible ? { ...current, visible: false } : current
      );
      return;
    }

    const tabsRect = tabsNode.getBoundingClientRect();
    const selectedRect = selectedTab.getBoundingClientRect();
    const nextIndicator = {
      top: Math.round(selectedRect.top - tabsRect.top),
      height: Math.round(selectedRect.height),
      visible: true,
    };

    setSidebarIndicator((current) => {
      const unchanged =
        current.visible === nextIndicator.visible &&
        Math.abs(current.top - nextIndicator.top) < 0.5 &&
        Math.abs(current.height - nextIndicator.height) < 0.5;

      return unchanged ? current : nextIndicator;
    });
  }, []);

  const cancelScheduledSidebarIndicatorUpdate = useCallback(() => {
    const { first, second } = sidebarIndicatorAnimationFramesRef.current;

    if (first !== undefined) {
      window.cancelAnimationFrame(first);
    }

    if (second !== undefined) {
      window.cancelAnimationFrame(second);
    }

    sidebarIndicatorAnimationFramesRef.current = {};
  }, []);

  const scheduleSidebarIndicatorUpdate = useCallback(
    (nextTab: false | number) => {
      cancelScheduledSidebarIndicatorUpdate();

      if (!sidebarIndicatorRef.current?.visible) {
        updateSidebarIndicator(nextTab);
        return;
      }

      sidebarIndicatorAnimationFramesRef.current.first =
        window.requestAnimationFrame(() => {
          sidebarIndicatorAnimationFramesRef.current.second =
            window.requestAnimationFrame(() => {
              sidebarIndicatorAnimationFramesRef.current = {};
              updateSidebarIndicator(nextTab);
            });
        });
    },
    [cancelScheduledSidebarIndicatorUpdate, updateSidebarIndicator]
  );

  useLayoutEffect(() => {
    if (deferFirstIndicatorMeasurement.current) {
      return;
    }

    updateSidebarIndicator(currentTabRef.current);
  }, [
    compact,
    dense,
    hideLabels,
    iconScale,
    tabConfig.length,
    tabPaddingY,
    updateSidebarIndicator,
  ]);

  useEffect(() => {
    if (deferFirstIndicatorMeasurement.current) {
      deferFirstIndicatorMeasurement.current = false;
      scheduleSidebarIndicatorUpdate(tab);
      return;
    }

    scheduleSidebarIndicatorUpdate(tab);
  }, [scheduleSidebarIndicatorUpdate, tab]);

  useEffect(() => {
    if (sidebarIndicator.visible) {
      lastSidebarIndicator = sidebarIndicator;
    }
  }, [sidebarIndicator]);

  useEffect(() => {
    const tabsNode = tabsRef.current;

    if (!tabsNode || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const scrollerNode =
      tabsNode.querySelector<HTMLElement>(".MuiTabs-scroller") ?? tabsNode;
    const observer = new ResizeObserver(() => {
      if (!deferFirstIndicatorMeasurement.current) {
        updateSidebarIndicator(currentTabRef.current);
      }
    });
    observer.observe(tabsNode);
    observer.observe(scrollerNode);
    scrollerNode
      .querySelectorAll<HTMLElement>('[role="tab"]')
      .forEach((tabElement) => observer.observe(tabElement));

    return () => observer.disconnect();
  }, [hideLabels, tabConfig.length, updateSidebarIndicator]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const navCallback = useCallback(
    (to: string) => {
      isLeftDrawerOpen && toggleLeftDrawer();
      navigate(to);
    },
    [isLeftDrawerOpen, toggleLeftDrawer, navigate]
  );

  const handleSidebarTabClick = useCallback(
    (newTab: number, to: string) => {
      setTab(newTab);
      navCallback(to);
    },
    [navCallback]
  );

  useEffect(
    () => () => {
      cancelScheduledSidebarIndicatorUpdate();
    },
    [cancelScheduledSidebarIndicatorUpdate]
  );

  return (
    <AppBar
      position="sticky"
      sx={({ palette, spacing }) => ({
        backgroundColor: palette.grey[50],
        boxShadow: "none",
        pt: isLargeScreen ? 0 : 2.5,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: isLargeScreen ? spacing(11) : "100%",
        flexShrink: 0,
      })}
    >
      {!isLargeScreen && (
        <Box sx={{ display: "flex", alignSelf: "center", p: 1 }}>
          <IconButton onClick={toggleLeftDrawer}>
            <BackIcon size={ICON_SIZE.control} />
          </IconButton>
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          py: isLargeScreen
            ? hideLabels
              ? 2
              : compact
              ? 4
              : 8
            : hideLabels
            ? 1
            : 4,
        }}
      >
        <RestaurantAvatar name={selectedRestaurant.name} />
      </Box>
      <Box
        ref={tabsRef}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
        }}
      >
        <Tabs
          TabIndicatorProps={{
            sx: {
              display: "none",
            },
          }}
          sx={{
            pt: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            flex: "1 1 auto",
            pb: 2,
            "& .MuiTabs-flexContainer": {
              display: "flex",
              flexDirection: "column",
              height: "100%",
              alignItems: "stretch",
            },
          }}
          orientation="vertical"
          textColor={"secondary"}
          value={tab}
          onChange={handleTabChange}
        >
          {tabConfig.map((t, index) => (
            <StyledTabs
              key={t.path}
              aria-label={hideLabels ? t.ariaLabel : undefined}
              label={hideLabels ? "" : t.label}
              icon={
                hideLabels ? (
                  <Tooltip title={t.ariaLabel} placement="right">
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {withDot(t.icon, settingsDot && t.path === "/settings")}
                    </Box>
                  </Tooltip>
                ) : (
                  withDot(t.icon, settingsDot && t.path === "/settings")
                )
              }
              onClick={() => handleSidebarTabClick(index, t.path)}
              data-dd-action-name={t.dataDdActionName}
              sx={{
                px: 2,
                py: tabPaddingY,
                ...(t.path === "/settings"
                  ? { mt: compact ? 0 : "auto", mb: 2 }
                  : {}),
                "& .MuiTab-iconWrapper": { transform: `scale(${iconScale})` },
              }}
            />
          ))}
        </Tabs>
        {sidebarIndicator.visible && (
          <Box
            aria-hidden
            sx={({ palette }) => ({
              position: "absolute",
              top: 0,
              right: 0,
              width: 2,
              height: sidebarIndicator.height,
              borderRadius: 9999,
              pointerEvents: "none",
              backgroundColor: palette.secondary.main,
              transform: `translate3d(0, ${sidebarIndicator.top}px, 0)`,
              transition: `transform ${motionDurations.slow}ms ${motionEasings.emphasized}`,
              willChange: "transform",
            })}
          />
        )}
      </Box>
    </AppBar>
  );
};

const StyledTabs = styled(Tab)(({ theme }) => ({
  color: theme.palette.grey[900],
  textAlign: "center",
  fontSize: "12px",
  lineHeight: 1.5,
  minWidth: 0,
  width: "100%",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  "& .igfd-admin-pill": {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[700],
  },
  "&.Mui-selected": {
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
  },
  "&.Mui-selected .igfd-admin-pill": {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
  },
}));

const RestaurantAvatar = ({ name }: { name: string }) => {
  //only taking initial on first word. e.g. "McDonalds" -> "M"
  const restaurantInitials = name.charAt(0).toUpperCase();

  // The default theme carries the logo; without one the initials are shown.
  const logoImageUrl = useSelector(
    (state: ReduxState) =>
      state.theme.themes.find((theme) => theme.id === DEFAULT_THEME_ID)
        ?.logoImageUrl || undefined
  );

  return (
    <Avatar
      sx={({ palette }) => ({
        bgcolor: logoImageUrl ? "transparent" : palette.primary.main,
        width: 48,
        height: 48,
        border: `1px solid ${palette.grey[300]}`,
        backgroundColor: palette.common.white,
      })}
    >
      {logoImageUrl ? (
        <img
          src={logoImageUrl}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        restaurantInitials
      )}
    </Avatar>
  );
};
